import { useCallback, useMemo } from "react";

/**
 * Hook for circular carousel navigation (wraps around at boundaries)
 * Used by FileGridPreview, VideoModal, and other carousel-like components
 */
export function useCarouselNavigation<T>({
    items,
    currentIndex,
    onNavigate
}: {
    items: T[];
    currentIndex: number;
    onNavigate: (item: T, index: number) => void;
}) {
    const handlePrevious = useCallback(() => {
        if (currentIndex === -1 || items.length === 0) return;

        const prevIndex = currentIndex <= 0 ? items.length - 1 : currentIndex - 1;
        onNavigate(items[prevIndex], prevIndex);
    }, [currentIndex, items, onNavigate]);

    const handleNext = useCallback(() => {
        if (currentIndex === -1 || items.length === 0) return;

        const nextIndex = currentIndex >= items.length - 1 ? 0 : currentIndex + 1;
        onNavigate(items[nextIndex], nextIndex);
    }, [currentIndex, items, onNavigate]);

    const canNavigate = useMemo(() => items.length > 1, [items.length]);

    return { handlePrevious, handleNext, canNavigate };
}
